import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { IndividualReportType } from 'chaika-enums';
import type { OptionType } from '@components/Select';

import { makeOptionType } from '@utils/makeOptionTypes';
import { isDefined } from '@utils/isDefined';
import { useIndividualReportRender } from '@modules/organization/ui/LegalEntityPage/pages/IndividualReportsPage/hooks/useIndividualReportRender';
import { individualReportQueryOptions } from '@organization/queries/individualReportQueries';
import { store } from '@store';

interface Params {
    legalEntityId: string;
    type: IndividualReportType;
    params: object;
}

interface UseIndividualReportPopup {
    selectedReport?: string;
    reportOptions: OptionType<string>[];
    canRenderReport: boolean;
    selectReport(option: OptionType<string>): void;
    renderReport(): void;
}

export function useIndividualReportPopup({ legalEntityId, type, params }: Params): UseIndividualReportPopup {
    const { app } = store;
    const { render } = useIndividualReportRender();

    const [selectedReport, setSelectedReport] = useState<string>();

    const { data: reports = [] } = useQuery(individualReportQueryOptions.active(legalEntityId, type));

    const reportOptions = makeOptionType(reports.map(({ id, title }) => ({ id, name: title ?? 'Report' })));

    const canRenderReport = isDefined(selectedReport);

    useEffect(() => {
        if (reportOptions.length === 1 && !selectedReport) {
            setSelectedReport(reportOptions[0].value);
        }
    }, [reportOptions, reportOptions.length, selectedReport]);

    function selectReport(option: OptionType<string>): void {
        setSelectedReport(option.value);
    }

    function renderReport() {
        const report = reports.find(({ id }) => id === selectedReport);

        if (report) {
            render(report, params);
            app.showPopup(null);
        }
    }

    return {
        selectedReport,
        reportOptions,
        canRenderReport,
        selectReport,
        renderReport,
    };
}
